/**
 * BundleBuilder-local RHF fork of CommonFields/Discount.tsx (shared across 8
 * bundle types — not editable in place). Only the BUNDLE_BUILDER branch of
 * the original component is ported: B2C/B2B toggle, pricing-strategy tiles
 * (Tiered / Fixed Boxes / Single / No Discount), tier discounts
 * (BBTierDiscounts), fixed boxes (FixedBoxesConfig), B2B discount options,
 * hide-prices toggle, and the order/product/shipping "combine discounts"
 * checkboxes (skip-key fields — always taken from Base/Variant A).
 */
import { useContext, useEffect, useRef, useState } from "react";
import styles from "../../CommonFields/CommonFields.module.css";
import {
    BlockStack,
    Button,
    ButtonGroup,
    Card,
    Collapsible,
    Divider,
    FormLayout,
    Icon,
    Label,
    Text,
} from "@shopify/polaris";
import { MdOutlineArrowBackIosNew, MdOutlineTrendingUp, MdOutlineBlock } from "react-icons/md";
import { BundleTypes, DISCOUNT_TYPES, DiscountTypes, getDiscountOptionsBB } from "../../constants";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { PackageIcon, DiscountIcon } from "@shopify/polaris-icons";
import { getNumberFieldSuffix } from "../../../../utilities/constants";
import MantleDoc, { mantleDocHandles } from "../../../../components/MantleDoc";
import { useFormContext, useWatch } from "react-hook-form";
import { FormCheckbox, FormSelect, FormTextField, FormToggle } from "../../../../components/FormFields";
import BBTierDiscounts from "./BBTierDiscounts";
import FixedBoxesConfig from "./FixedBoxesConfig";

const PricingStrategyTile = ({
    title,
    description,
    icon,
    selected,
    onClick,
}: {
    title: string;
    description: string;
    icon: any;
    selected: boolean;
    onClick: () => void;
}) => (
    <div
        onClick={onClick}
        className={styles.pricingTile}
        style={{
            border: selected ? "2px solid #008060" : "1px solid #E1E3E5",
            backgroundColor: selected ? "#F0FDF4" : "white",
        }}
    >
        {selected && <div className={styles.selectionDot} />}
        <BlockStack gap="200">
            <div className={styles.tileIconWrapper}>
                <Icon source={icon} tone={selected ? "success" : "base"} />
            </div>
            <BlockStack gap="0">
                <Text as="span" variant="bodyMd" fontWeight="bold">
                    {title}
                </Text>
                <Text as="span" variant="bodySm" tone="subdued">
                    {description}
                </Text>
            </BlockStack>
        </BlockStack>
    </div>
);

const B2bDiscountOptions = () => {
    const { control, setValue } = useFormContext();
    const b2bDiscountValue = useWatch({ control, name: "b2b_discount_value" });
    const b2bDiscountsEnabled = useWatch({ control, name: "b2b_discounts_enabled" });

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <FormToggle name="b2b_discounts_enabled" zoom={0.8} />
                <Label id="b2b-discounts-toggle">Turn on B2B discounts</Label>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <Text as="h5" alignment="center">
                    Percentage Discount
                </Text>
                <FormTextField
                    name="b2b_discount_value"
                    type="number"
                    label="Value"
                    labelHidden
                    placeholder="value"
                    min={0}
                    suffix="%"
                    value={(b2bDiscountValue ?? 0).toString()}
                    onChange={(val) => {
                        const regex = /^(\d+(\.\d{0,2})?)?$/;
                        if (regex.test(val)) {
                            if (+val > 100) val = "100";
                            setValue("b2b_discount_value", +val, { shouldDirty: true });
                        }
                    }}
                    disabled={b2bDiscountsEnabled == false}
                />
            </div>
        </div>
    );
};

const Discount = () => {
    const { control, setValue } = useFormContext();
    const discountType = useWatch({ control, name: "discount_type" }) as DiscountTypes;
    const discountValue = useWatch({ control, name: "discount_value" });
    const hidePricesProductCard = useWatch({ control, name: "hide_prices_product_card" });
    const hideBundleHeader = useWatch({ control, name: "hide_bundle_header" });
    const fixedBoxes = useWatch({ control, name: "fixed_boxes" });

    const autoHiddenRef = useRef(false);
    const [expandDiscount, setExpandDiscount] = useState(false);
    const [expandDiscountCombination, setExpandDiscountCombination] = useState(true);
    const [isB2bEnabled, setIsB2bEnabled] = useState(false);
    const appConfigContext = useContext(AppConfigContext);
    const { money_format, shop } = appConfigContext;
    const { trackFormSections } = useBundleMixpanelHook();

    const [lastStandardType, setLastStandardType] = useState<DiscountTypes>(
        ["tier", DISCOUNT_TYPES.FIXED_BOXES].includes(discountType) ? "percentage" : discountType,
    );
    useEffect(() => {
        if (discountType !== "tier" && discountType !== DISCOUNT_TYPES.FIXED_BOXES) {
            setLastStandardType(discountType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discountType]);

    useEffect(() => {
        const currentValue = +discountValue || 0;
        if (discountType === "percentage" && currentValue > 100) {
            setValue("discount_value", 100, { shouldDirty: true });
        }
        if (discountType === "none") {
            setValue("discount_value", 1, { shouldDirty: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [discountType]);

    const BundleBuilderFilteredOptions = getDiscountOptionsBB(money_format.split("{{")[0].trim()).filter(() => true);

    const autoUnhideHeader = () => {
        if (autoHiddenRef.current && hideBundleHeader) {
            setValue("hide_bundle_header", false, { shouldDirty: true });
            autoHiddenRef.current = false;
        }
    };

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandDiscount((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Discount ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.BUNDLE_BUILDER,
                        });
                        return !ps;
                    })
                }
            >
                <MantleDoc handle={mantleDocHandles[BundleTypes.BUNDLE_BUILDER].setUpDiscounts} title="Discounts" />
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandDiscount ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandDiscount}
                id="basic-collapsible"
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
            >
                <FormLayout>
                    <div className={`${styles.formRow} `} style={{ gridTemplateColumns: "1fr", gap: "16px" }}>
                        {[
                            "quickstart-f02489ba",
                            "newteststore-290133",
                            "trustbadgetest",
                            "javv39-ze",
                            "sdhieur",
                        ].includes(shop) && (
                            <>
                                <ButtonGroup variant="segmented">
                                    <Button pressed={isB2bEnabled === false} onClick={() => setIsB2bEnabled(false)}>
                                        B2C
                                    </Button>
                                    <Button pressed={isB2bEnabled === true} onClick={() => setIsB2bEnabled(true)}>
                                        B2B
                                    </Button>
                                </ButtonGroup>
                                <Divider />
                            </>
                        )}

                        {!isB2bEnabled && (
                            <BlockStack gap="300">
                                <Text as="h3" variant="headingSm" fontWeight="bold">
                                    1. Choose Pricing Strategy
                                </Text>
                                <div className={styles.tilesGrid}>
                                    <PricingStrategyTile
                                        title="Tiered"
                                        description="Buy more, save more"
                                        icon={MdOutlineTrendingUp}
                                        selected={discountType == "tier"}
                                        onClick={() => {
                                            setValue("discount_type", "tier", { shouldDirty: true });
                                            autoUnhideHeader();
                                        }}
                                    />
                                    <PricingStrategyTile
                                        title="Fixed Boxes"
                                        description="Set box sizes"
                                        icon={PackageIcon}
                                        selected={discountType === DISCOUNT_TYPES.FIXED_BOXES}
                                        onClick={() => {
                                            setValue("discount_type", DISCOUNT_TYPES.FIXED_BOXES, {
                                                shouldDirty: true,
                                            });
                                            if ((fixedBoxes || []).length === 0) {
                                                setValue(
                                                    "fixed_boxes",
                                                    [{ itemsCount: 2, fixedPrice: 50, boxName: "Box of 2" }],
                                                    { shouldDirty: true },
                                                );
                                            }
                                            if (!hideBundleHeader) {
                                                setValue("hide_bundle_header", true, { shouldDirty: true });
                                                autoHiddenRef.current = true;
                                            }
                                        }}
                                    />
                                    <PricingStrategyTile
                                        title="Single"
                                        description="Build & save"
                                        icon={DiscountIcon}
                                        selected={
                                            discountType !== "tier" &&
                                            discountType !== DISCOUNT_TYPES.FIXED_BOXES &&
                                            discountType !== DISCOUNT_TYPES.NONE
                                        }
                                        onClick={() => {
                                            const payload =
                                                lastStandardType === DISCOUNT_TYPES.NONE
                                                    ? DISCOUNT_TYPES.PERCENTAGE
                                                    : lastStandardType;
                                            setValue("discount_type", payload, { shouldDirty: true });
                                            autoUnhideHeader();
                                        }}
                                    />
                                    <PricingStrategyTile
                                        title="No Discount"
                                        description="Standard pricing"
                                        icon={MdOutlineBlock}
                                        selected={discountType === DISCOUNT_TYPES.NONE}
                                        onClick={() => {
                                            setValue("discount_type", DISCOUNT_TYPES.NONE, { shouldDirty: true });
                                            autoUnhideHeader();
                                        }}
                                    />
                                </div>
                            </BlockStack>
                        )}
                    </div>

                    {discountType == DISCOUNT_TYPES.TIER && !isB2bEnabled && (
                        <div className={`${styles.formRow} `} style={{ gridTemplateColumns: "1fr" }}>
                            <BBTierDiscounts />
                        </div>
                    )}

                    {discountType == DISCOUNT_TYPES.FIXED_BOXES && !isB2bEnabled && (
                        <div className={`${styles.formRow} `} style={{ gridTemplateColumns: "1fr", minWidth: 0 }}>
                            <FixedBoxesConfig />
                        </div>
                    )}

                    {discountType != DISCOUNT_TYPES.TIER &&
                        discountType != DISCOUNT_TYPES.NONE &&
                        discountType != DISCOUNT_TYPES.FIXED_BOXES &&
                        !isB2bEnabled && (
                            <BlockStack gap="200">
                                <Text as="h3" variant="headingSm" fontWeight="bold">
                                    3. Configure Offer
                                </Text>
                                <Card background="bg-surface-secondary">
                                    <div className={`${styles.formRow} `}>
                                        <FormSelect
                                            name="discount_type"
                                            label="Discount Type"
                                            placeholder="Select discount type"
                                            options={BundleBuilderFilteredOptions.filter(
                                                (option) => option.value !== DISCOUNT_TYPES.NONE,
                                            )}
                                            onChange={(val) => {
                                                setValue("discount_type", val, { shouldDirty: true });
                                                setLastStandardType(val as DiscountTypes);
                                            }}
                                            value={discountType}
                                        />
                                        <FormTextField
                                            name="discount_value"
                                            type="number"
                                            label="Value"
                                            placeholder="value"
                                            min={0}
                                            suffix={getNumberFieldSuffix(discountType, money_format).suffix}
                                            value={(discountValue ?? 0).toString()}
                                            onChange={(val) => {
                                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                                if (regex.test(val)) {
                                                    if (discountType === "percentage" && +val > 100) val = "100";
                                                    setValue("discount_value", +val, { shouldDirty: true });
                                                }
                                            }}
                                        />
                                    </div>
                                </Card>
                            </BlockStack>
                        )}

                    {isB2bEnabled && <B2bDiscountOptions />}

                    {discountType == DISCOUNT_TYPES.FIXED && (
                        <div className={`${styles.formRow} `} style={{ gridTemplateColumns: "1fr" }}>
                            <FormCheckbox
                                label="Hide Prices on Product Card"
                                name="hide_prices_product_card"
                                checked={!!hidePricesProductCard}
                            />
                        </div>
                    )}

                    <div
                        className={styles.flexHorizontalContainer}
                        onClick={() => setExpandDiscountCombination((prev) => !prev)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            padding: "8px 0",
                        }}
                    >
                        <p className={styles.combineTitle} style={{ margin: 0 }}>
                            Combine this discount with other
                        </p>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                transform: expandDiscountCombination ? "rotate(90deg)" : "rotate(270deg)",
                            }}
                        >
                            <MdOutlineArrowBackIosNew size={12} />
                        </div>
                    </div>
                    <Collapsible
                        open={expandDiscountCombination}
                        transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                        expandOnPrint
                        id="discount-combination-collapsible"
                    >
                        <div
                            className={`${styles.formCol}`}
                            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
                        >
                            <FormCheckbox label="Order discounts" name="order_discounts" />
                            <FormCheckbox label="Product discounts" name="product_discounts" />
                            <FormCheckbox label="Shipping discounts" name="shipping_discounts" />
                        </div>
                    </Collapsible>
                </FormLayout>
            </Collapsible>
        </>
    );
};

export default Discount;
